import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface PlatformStats {
  // Homepage stats (approved users only for credibility)
  approved_partners: number;
  approved_youth: number;
  completed_projects: number;
  
  // Admin dashboard stats (total counts for operations)
  total_users: number;
  total_partners: number;
  total_youth: number;
  pending_approvals: number;
}

/**
 * Animated counter hook: smoothly counts from prev → next over `duration` ms
 */
export function useAnimatedCounter(target: number, duration = 800) {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const frameRef = useRef<number>();

  useEffect(() => {
    const from = prevRef.current;
    const to = target;
    if (from === to) return;

    const start = performance.now();

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        prevRef.current = to;
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration]);

  return display;
}

/**
 * Unified platform statistics hook - single source of truth for all metrics
 * Homepage shows approved counts for credibility, admin shows totals for operations
 */
export function usePlatformStats() {
  const [stats, setStats] = useState<PlatformStats>({
    approved_partners: 0,
    approved_youth: 0,
    completed_projects: 58,
    total_users: 0,
    total_partners: 0,
    total_youth: 0,
    pending_approvals: 0,
  });
  const [glowPulse, setGlowPulse] = useState(false);
  const initialLoad = useRef(true);

  useEffect(() => {
    // Fetch all stats from source tables for consistency
    const fetchStats = async () => {
      try {
        // Use the new RPC function to get all metrics consistently
        const { data, error } = await supabase
          .rpc('get_platform_statistics');

        if (error) {
          console.error("Error fetching platform statistics:", error);
          return;
        }

        if (data && Array.isArray(data) && data.length > 0) {
          const metrics = data[0];
          const newStats = {
            approved_partners: Number(metrics.approved_partners) || 0,
            approved_youth: Number(metrics.approved_youth) || 0,
            completed_projects: (Number(metrics.completed_projects) || 0) + 58,
            total_users: Number(metrics.total_users) || 0,
            total_partners: Number(metrics.total_partners) || 0,
            total_youth: Number(metrics.total_youth) || 0,
            pending_approvals: Number(metrics.pending_approvals) || 0,
          };

          // Trigger glow effect if numbers changed (but not on initial load)
          if (!initialLoad.current) {
            if (JSON.stringify(stats) !== JSON.stringify(newStats)) {
              setGlowPulse(true);
              setTimeout(() => setGlowPulse(false), 1500);
            }
          }

          setStats(newStats);
          initialLoad.current = false;
        }
      } catch (error) {
        console.error("Error fetching platform statistics:", error);
      }
    };

    fetchStats();

  }, [stats]);

  return { stats, glowPulse };
}
